import { DEFAULT_LANGUAGE, LANGUAGE_CODE_PATTERN } from '../constants/locale.constants';

export function normalizeLocaleTag(tag: string): string {
  const [language, region, ...rest] = tag.trim().replace(/_/g, '-').split('-');
  return [language?.toLowerCase(), region?.toUpperCase(), ...rest].filter((part) => !!part).join('-');
}

export function getBaseLanguage(tag: string): string {
  return normalizeLocaleTag(tag).split('-')[0] ?? '';
}

export function isLanguageCode(value: unknown): value is string {
  return typeof value === 'string' && LANGUAGE_CODE_PATTERN.test(value);
}

// The language's name in its own language ("Deutsch", "English") via Intl.DisplayNames — no translation keys needed.
export function getLanguageEndonym(language: string): string {
  const name = new Intl.DisplayNames([language], { type: 'language' }).of(language);
  if (!name) return language.toUpperCase();
  return name.charAt(0).toUpperCase() + name.slice(1);
}

// Returns the first candidate whose base language is in `supported`, in order, so ['fr-FR', 'en-US']
// matches 'en'. Matches on the base language; regional refinement comes from the tenant country via
// resolveEffectiveLocale, not the browser tag. `supported` is caller-provided: the user-selectable
// languages (DB-driven) or the locales an artifact ships bundles for.
export function matchPreferredLanguage(candidates: readonly string[], supported: readonly string[]): string | null {
  const normalizedSupported = supported.map((tag) => normalizeLocaleTag(tag));
  for (const candidate of candidates) {
    const base = getBaseLanguage(candidate);
    if (isLanguageCode(base) && normalizedSupported.includes(base)) return base;
  }
  return null;
}

// availableLocales is a parameter because availability differs per artifact (frontend, backend, email, Strapi).
// Resolution order: requested language regional → base, then default language regional → base. A language
// without any bundle (e.g. newly added in the DB before translations ship) thus renders exactly like a
// default-language user of the same tenant, including the country overlay.
export function resolveEffectiveLocale(
  baseLanguage: string | null | undefined,
  countryIso: string | null | undefined,
  availableLocales: readonly string[],
): string {
  const base = baseLanguage != null ? getBaseLanguage(baseLanguage) : '';
  const requested = isLanguageCode(base) ? base : DEFAULT_LANGUAGE;
  const normalizedAvailable = availableLocales.map((tag) => normalizeLocaleTag(tag));
  const region = countryIso?.trim().toUpperCase();

  for (const language of new Set([requested, DEFAULT_LANGUAGE])) {
    if (region && normalizedAvailable.includes(`${language}-${region}`)) return `${language}-${region}`;
    if (normalizedAvailable.includes(language)) return language;
  }
  return DEFAULT_LANGUAGE;
}
